import { observable, action } from 'mobx';
import { identify, track } from 'utils/analytics';
import { request, handleJwtError } from 'utils/api';
import BaseStore from './BaseStore';

// Braze custom attributes (sent via GTM) use human-readable identifiers
// rather than ids. Add new favorite-able types here as they're supported.
const FAVORITE_TRACKING = {
  venues: {
    fetchMethod: 'fetchFavoriteVenues',
    attribute: 'favoriteVenues',
    getValue: (venue) => venue.slug,
  },
  events: {
    fetchMethod: 'fetchFavoriteEvents',
    attribute: 'favoriteEvents',
    getValue: (event) => event.slug,
  },
  characters: {
    fetchMethod: 'fetchFavoriteCharacters',
    attribute: 'favoriteCharacters',
    getValue: (character) => character.name,
  },
};

export default class MeStore extends BaseStore {
  @observable user;
  @observable upcomingOrdersCount;
  @observable favoriteVenues = [];

  promise = null;

  @action
  fetch(statusKey) {
    if (this.promise && statusKey === 'boot') {
      return this.promise;
    } else {
      const status = this.createStatus(statusKey);
      const promise = request({
        method: 'GET',
        path: '/1/users/me',
      })
        .then((resp) => {
          this.user = resp.data;
          status.success();
          return this.user;
        })
        .catch((err) => {
          handleJwtError(err);
          status.error(err);
          throw err;
        });
      if (statusKey === 'boot') {
        this.promise = promise;
      }
      return promise;
    }
  }

  @action
  update(body, statusKey = 'update') {
    const status = this.createStatus(statusKey);
    this.promise = request({
      method: 'PATCH',
      path: '/1/users/me',
      body,
    })
      .then((resp) => {
        this.user = resp.data;
        status.success();
        if (body.licensePlate) {
          track('Set Properties', { licensePlate: body.licensePlate });
        }
        return this.user;
      })
      .catch((err) => {
        status.error(err);
        return err;
      });
    return this.promise;
  }

  @action
  setFavorite(type, itemId, checked, statusKey = 'favorite') {
    const status = this.createStatus(statusKey);

    // Optimistically reflect the change so the UI updates instantly. If the
    // request fails we restore the previous user below.
    const previousUser = this.user;
    if (this.user) {
      const favorites = { ...(this.user.favorites || {}) };
      const current = new Set(favorites[type] || []);
      if (checked) {
        current.add(itemId);
      } else {
        current.delete(itemId);
      }
      favorites[type] = Array.from(current);
      this.user = { ...this.user, favorites };
    }

    this.promise = request({
      method: checked ? 'POST' : 'DELETE',
      path: `/1/users/me/favorites/${type}/${itemId}`,
    })
      .then((resp) => {
        this.user = resp.data;
        status.success();
        const tracking = FAVORITE_TRACKING[type];
        if (tracking) {
          this[tracking.fetchMethod]().then((items) => {
            if (Array.isArray(items)) {
              track('Set Properties', {
                [tracking.attribute]: items.map(tracking.getValue),
              });
            }
          });
        }
        return this.user;
      })
      .catch((err) => {
        // Roll back the optimistic update.
        this.user = previousUser;
        status.error(err);
        return err;
      });
    return this.promise;
  }

  isFavorite(type, itemId) {
    return Boolean(this.user?.favorites?.[type]?.includes(itemId));
  }

  @action
  fetchFavoriteVenues(statusKey = 'fetchFavoriteVenues') {
    const status = this.createStatus(statusKey);
    return request({
      method: 'GET',
      path: '/1/users/me/favorites/venues',
    })
      .then((resp) => {
        status.success();
        this.favoriteVenues = resp.data;
        return resp.data;
      })
      .catch((err) => {
        status.error(err);
        return err;
      });
  }

  @action
  fetchFavoriteEvents(statusKey = 'fetchFavoriteEvents') {
    const status = this.createStatus(statusKey);
    return request({
      method: 'GET',
      path: '/1/users/me/favorites/events',
    })
      .then((resp) => {
        status.success();
        return resp.data;
      })
      .catch((err) => {
        status.error(err);
        return err;
      });
  }

  @action
  fetchFavoriteEventsSummary(statusKey = 'fetchFavoriteEventsSummary') {
    const status = this.createStatus(statusKey);
    return request({
      method: 'GET',
      path: '/1/users/me/favorites/events/summary',
    })
      .then((resp) => {
        status.success();
        return resp.data;
      })
      .catch((err) => {
        status.error(err);
        return err;
      });
  }

  @action
  fetchFavoriteCharacters(statusKey = 'fetchFavoriteCharacters') {
    const status = this.createStatus(statusKey);
    return request({
      method: 'GET',
      path: '/1/users/me/favorites/characters',
    })
      .then((resp) => {
        status.success();
        return resp.data;
      })
      .catch((err) => {
        status.error(err);
        return err;
      });
  }

  @action
  fetchSubscriptionGroups(statusKey = 'fetchSubscriptionGroups') {
    const status = this.createStatus(statusKey);
    return request({
      method: 'GET',
      path: '/1/users/me/subscription-groups',
    })
      .then((resp) => {
        status.success();
        return resp.data;
      })
      .catch((err) => {
        status.error(err);
        return err;
      });
  }

  @action
  updateSubscriptionGroup(
    group,
    active,
    statusKey = 'updateSubscriptionGroup'
  ) {
    const status = this.createStatus(statusKey);
    return request({
      method: 'PATCH',
      path: `/1/users/me/subscription-groups/${group}`,
      body: { active },
    })
      .then((resp) => {
        status.success();
        return resp.data;
      })
      .catch((err) => {
        status.error(err);
        return err;
      });
  }

  @action
  fetchFavoriteRecommendations(statusKey = 'fetchFavoriteRecommendations') {
    const status = this.createStatus(statusKey);
    return request({
      method: 'GET',
      path: '/1/users/me/favorites/recommendations',
    })
      .then((resp) => {
        status.success();
        return resp.data;
      })
      .catch((err) => {
        status.error(err);
        return err;
      });
  }

  @action
  fetchUpcomingOrdersCount(statusKey = 'getUpcomingOrdersCount') {
    const status = this.createStatus(statusKey);
    return request({
      method: 'GET',
      path: '/1/users/me/orders/upcoming-count',
    })
      .then(({ data }) => {
        status.success();
        this.upcomingOrdersCount = data.count;
        return data.count;
      })
      .catch((err) => {
        status.error(err);
        return 0;
      });
  }

  @action
  uploadAvatar(file, statusKey = 'updateAvatar') {
    const status = this.createStatus(statusKey);
    return request({
      method: 'POST',
      path: '/1/users/me/avatar',
      file,
    })
      .then((resp) => {
        this.user = resp.data;
        status.success();
        return this.user;
      })
      .catch((err) => {
        status.error(err);
        return err;
      });
  }

  @action
  deleteAccount(statusKey = 'deleteAccount') {
    const status = this.createStatus(statusKey);
    return request({
      method: 'DELETE',
      path: '/1/users/me',
    })
      .then(() => {
        status.success();
      })
      .catch((err) => {
        status.error(err);
        return err;
      });
  }

  @action
  removeAvatar(statusKey = 'updateAvatar') {
    const status = this.createStatus(statusKey);
    return request({
      method: 'DELETE',
      path: '/1/users/me/avatar',
    })
      .then((resp) => {
        this.user = resp.data;
        status.success();
        return this.user;
      })
      .catch((err) => {
        status.error(err);
        return err;
      });
  }

  @action
  updatePhone({ userId, phone }, statusKey = 'updatePhone') {
    const status = this.createStatus(statusKey);
    return request({
      method: 'PUT',
      path: '/1/auth/phone',
      body: {
        userId,
        phone,
      },
    })
      .then(() => {
        this.user.phone = phone;
        identify(this.user);
        track('Set Properties', { phone });
        status.success();
      })
      .catch((err) => {
        status.error(err);
        throw err;
      });
  }
}
